import React, { useState, useEffect } from "react";
import Pagination from "@/components/pagination";
import SearchInput from "@/components/search-input";
import API from "@/services/api";
import toast from "react-hot-toast";
import ApplicationDetailsModal from "../components/ApplicationDetailsModal";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    total: "Total",
    no_applications: "No applications found",
    table: {
      candidate: "Candidate",
      job: "Job",
      organization: "Organization",
      status: "Status",
      source: "Source",
      match_score: "Match Score",
      resume_status: "Resume",
      coaching_status: "Coaching",
      applied_date: "Applied Date",
    },
    filters: {
      all_statuses: "All Statuses",
      all_resume: "All Resume Status",
      all_coaching: "All Coaching Status",
    },
  },
  fr: {
    total: "Total",
    no_applications: "Aucune candidature trouvée",
    table: {
      candidate: "Candidat",
      job: "Poste",
      organization: "Organisation",
      status: "Statut",
      source: "Source",
      match_score: "Score",
      resume_status: "CV",
      coaching_status: "Coaching",
      applied_date: "Date de candidature",
    },
    filters: {
      all_statuses: "Tous les statuts",
      all_resume: "Tous les statuts CV",
      all_coaching: "Tous les statuts Coaching",
    },
  },
};

const STATUS_COLORS = {
  APPLIED: "bg-blue-100 text-blue-800",
  PRESELECTED: "bg-yellow-100 text-yellow-800",
  INTERVIEW: "bg-purple-100 text-purple-800",
  SELECTED: "bg-green-100 text-green-800",
  REFUSED: "bg-red-100 text-red-800",
  EXPIRED: "bg-gray-100 text-gray-800",
};

const PROCESSING_STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  done: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-800",
};

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedResumeStatus, setSelectedResumeStatus] = useState("");
  const [selectedCoachingStatus, setSelectedCoachingStatus] = useState("");
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const limit = 25;
  const { t } = useComponentTranslation(translations);

  useEffect(() => {
    fetchApplications();
  }, [currentPage, searchTerm, selectedStatus, selectedResumeStatus, selectedCoachingStatus, selectedOrganization]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await API.post("/application/search", {
        page: currentPage,
        limit: limit,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(selectedOrganization && { organization_id: selectedOrganization }),
      });

      if (response.ok) {
        let data = response.data || [];

        // Client-side filtering for processing statuses (not supported by API)
        if (selectedResumeStatus) {
          data = data.filter((app) => app.resume_parse_status === selectedResumeStatus);
        }
        if (selectedCoachingStatus) {
          data = data.filter((app) => app.coaching_insights_status === selectedCoachingStatus);
        }

        setApplications(data);
        setTotal(response.total || 0);
      } else {
        toast.error("Failed to load applications");
      }
    } catch (e) {
      console.error("Error fetching applications:", e);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
  };

  return (
    <div className="p-6">
      {/* Filters */}
      <div className="mb-3 flex flex-wrap gap-3">
        <SearchInput
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          className="w-64"
          placeholder="Search candidate or job..."
        />
        <OrganizationSelect
          value={selectedOrganization}
          onChange={(value) => {
            setSelectedOrganization(value);
            setCurrentPage(1);
          }}
        />
        <StatusSelect
          value={selectedStatus}
          onChange={(value) => {
            setSelectedStatus(value);
            setCurrentPage(1);
          }}
          placeholder={t("filters.all_statuses")}
          options={["APPLIED", "PRESELECTED", "INTERVIEW", "SELECTED", "REFUSED"]}
        />
        <StatusSelect
          value={selectedResumeStatus}
          onChange={(value) => {
            setSelectedResumeStatus(value);
            setCurrentPage(1);
          }}
          placeholder={t("filters.all_resume")}
          options={["pending", "done", "error"]}
        />
        <StatusSelect
          value={selectedCoachingStatus}
          onChange={(value) => {
            setSelectedCoachingStatus(value);
            setCurrentPage(1);
          }}
          placeholder={t("filters.all_coaching")}
          options={["pending", "done", "error"]}
        />
      </div>

      {/* Total and Pagination */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">
          {t("total")}: <span className="font-semibold text-gray-900">{total}</span>
        </div>
        <Pagination total={total} limit={limit} currentPage={currentPage} onPageChange={setCurrentPage} />
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t("no_applications")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3">{t("table.candidate")}</th>
                  <th className="px-4 py-3">{t("table.job")}</th>
                  <th className="px-4 py-3">{t("table.organization")}</th>
                  <th className="px-4 py-3">{t("table.status")}</th>
                  <th className="px-4 py-3">{t("table.source")}</th>
                  <th className="px-4 py-3">{t("table.match_score")}</th>
                  <th className="px-4 py-3">{t("table.resume_status")}</th>
                  <th className="px-4 py-3">{t("table.coaching_status")}</th>
                  <th className="px-4 py-3">{t("table.applied_date")}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {applications.map((application) => {
                  const appId = application._id || application.id;
                  return (
                    <tr key={appId} onClick={() => handleViewApplication(application)} className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer">
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{application.candidate_name || "-"}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        <div className="max-w-[200px] truncate" title={application.job_title}>
                          {application.job_title || "-"}
                        </div>
                        {application.job_code && <div className="text-xs text-gray-500">{application.job_code}</div>}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{application.organization_name || "-"}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${STATUS_COLORS[application.status] || "bg-gray-100 text-gray-800"}`}>
                          {application.status || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{application.source || "-"}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">
                        {application.coaching_match_score !== undefined ? (
                          <span className={`font-semibold ${application.coaching_match_score >= 70 ? "text-green-600" : application.coaching_match_score >= 50 ? "text-yellow-600" : "text-red-600"}`}>
                            {application.coaching_match_score}%
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PROCESSING_STATUS_COLORS[application.resume_parse_status] || "bg-gray-100 text-gray-800"}`}>
                          {application.resume_parse_status || "pending"}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PROCESSING_STATUS_COLORS[application.coaching_insights_status] || "bg-gray-100 text-gray-800"}`}>
                          {application.coaching_insights_status || "pending"}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {application.application_date ? new Date(application.application_date).toLocaleDateString("fr-FR") : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ApplicationDetailsModal isOpen={!!selectedApplication} onClose={() => setSelectedApplication(null)} application={selectedApplication} />
    </div>
  );
};

// Organization Select Component
const OrganizationSelect = ({ value, onChange }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await API.post("/organization/search", { approved: true });

      if (response.ok) {
        setOrganizations(response.data || []);
      } else {
        toast.error("Failed to load organizations");
      }
    } catch (e) {
      console.error("Error fetching organizations:", e);
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-w-[200px]"
        disabled={loading}
      >
        <option value="">All Organizations</option>
        {organizations.map((org) => (
          <option key={org._id} value={org._id}>
            {org.name}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

// Generic Status Select Component
const StatusSelect = ({ value, onChange, placeholder, options }) => {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-w-[150px]"
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

export default Applications;
